import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

interface TableState {
    [tableId: string]: {
        columnVisibility: { [columnId: string]: boolean };
    };
}

const TABLE_COLUMN_ID_MIGRATIONS: Record<string, Record<string, string>> = {
    'contracts-table': {
        currentContractBlockedStatusDate: 'currentBlockedStatusDate',
    },
};

const migrateColumnVisibility = (
    tableId: string,
    columnVisibility: { [columnId: string]: boolean }
): { [columnId: string]: boolean } => {
    const migrations = TABLE_COLUMN_ID_MIGRATIONS[tableId];
    if (!migrations) {
        return columnVisibility;
    }

    const migratedVisibility = { ...columnVisibility };

    Object.entries(migrations).forEach(([oldColumnId, newColumnId]) => {
        if (!(oldColumnId in migratedVisibility)) {
            return;
        }

        if (!(newColumnId in migratedVisibility)) {
            migratedVisibility[newColumnId] = migratedVisibility[oldColumnId];
        }

        delete migratedVisibility[oldColumnId];
    });

    return migratedVisibility;
};

const initialState: TableState = {};

const tableSlice = createSlice({
    name: 'table',
    initialState,
    reducers: {
        setColumnVisibility: (
            state,
            action: PayloadAction<{ tableId: string; columnId: string; isVisible: boolean }>
        ) => {
            const { tableId, columnId, isVisible } = action.payload;
            if (!state[tableId]) {
                state[tableId] = { columnVisibility: {} };
            }
            state[tableId].columnVisibility[columnId] = isVisible;
        },
        initializeColumnVisibility: (
            state,
            action: PayloadAction<{ tableId: string; columns: { columnId: string; isVisible: boolean }[] }>
        ) => {
            const { tableId, columns } = action.payload;
            state[tableId] = { columnVisibility: {} };
            columns.forEach(({ columnId, isVisible }) => {
                state[tableId].columnVisibility[columnId] = isVisible;
            });
        },
    },
});

export const { setColumnVisibility, initializeColumnVisibility } = tableSlice.actions;

export const selectColumnVisibility = (state: RootState, tableId: string) => state.table[tableId]?.columnVisibility || {};

export const getColumnVisibility = (table: any, tableId: string) => {
    const columnVisibility = table[tableId]?.columnVisibility || {};
    return migrateColumnVisibility(tableId, columnVisibility);
}
export default tableSlice.reducer;